import {TopicModel} from "@/models/TopicModel";
import {DebugLogger} from "@/utils/DebugLogger";

type searchItemResult = {
    score: number;
    topic: TopicModel;
}

export class SearchHelper {    
    public static filterTopics(topics: TopicModel[], searchFilter: string): TopicModel[] {
        const exactOnly = this.filterHasQuotes(searchFilter);
        searchFilter = this.getFilterWithoutQuotes(searchFilter);
        
        const filterArr = exactOnly ? [searchFilter] : searchFilter.split(" ").filter(f => f.length > 0);
        const multiWordSearch = !exactOnly && filterArr.length > 1;

        DebugLogger.time("Matching all filters with score");
        
        const topicStack: searchItemResult[] = topics.map(t => {
            let titleScore = this.getWordHitCountScore(filterArr, t.title, 1, 0.5);
            titleScore += multiWordSearch ? this.getMultiWordHitScore(searchFilter, t.title, 2) : 0;
            
            let leadScore = this.getWordHitCountScore(filterArr, t.lead || "", 0.75, 0.4);
            leadScore += multiWordSearch ? this.getMultiWordHitScore(searchFilter, t.lead || "", 1.5) : 0;
            
            let keywordsScore = this.getWordHitCountScore(filterArr, t.keywords || "", 0.05, 0.025);
            keywordsScore += multiWordSearch ? this.getMultiWordHitScore(searchFilter, t.keywords || "", 0.1) : 0;
            
            return {
                score: titleScore + leadScore + keywordsScore,
                scoreData: {
                    title: titleScore,
                    lead: leadScore,
                    keywords: keywordsScore
                },
                topic: t
            };
        })
            .filter(item => item.score > 0)
            .sort((a, b) => b.score - a.score);

        DebugLogger.log("Topic stack", topicStack);
        
        const result = topicStack
            .map(item => item.topic);

        DebugLogger.timeEnd("Matching all filters with score");
        
        return result;
    }

    private static getMultiWordHitScore(searchTerm: string, searchIn: string, multiWordHitScore: number): number {
        return searchIn.toLowerCase().indexOf(searchTerm) !== -1 ? multiWordHitScore : 0;
    }

    private static getWordHitCountScore(searchTerms: string[], searchIn: string, wordHitScore: number, wordHitInsideScore: number): number {
        return searchTerms.reduce((score, term) => {
            const boundaryRegex = new RegExp(`\\b${term}`, 'gi'); // Regex for word boundary matches (word starts with)
            const normalRegex = new RegExp(term, 'gi'); // Regex for normal matches

            const boundaryMatches = searchIn.match(boundaryRegex); // Find all boundary matches
            const normalMatches = searchIn.match(normalRegex); // Find all normal matches

            const boundaryMatchesCount = boundaryMatches ? boundaryMatches.length : 0; // Count of boundary matches
            const normalMatchesCount = normalMatches ? normalMatches.length : 0; // Count of normal matches
                        
            // Calculate score with 1.5x for boundary matches and normal score for the rest
            const boundaryScore = boundaryMatchesCount * wordHitScore;
            const normalScore = (normalMatchesCount - boundaryMatchesCount) * wordHitInsideScore;

            return score + boundaryScore + normalScore; // Add to the total score
        }, 0);
    }

    private static filterHasQuotes(searchFilter = "") {
        return searchFilter.length ? searchFilter.startsWith('"') && searchFilter.endsWith('"') : false;
    }

    private static getFilterWithoutQuotes(searchFilter: string) {
        return this.filterHasQuotes(searchFilter) ? searchFilter.substr(1, searchFilter.length - 2) : searchFilter;
    }
}
